import axios, {
  AxiosInstance,
  AxiosResponse,
  CancelTokenSource,
  InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios';

import { resolveInternalApiUrl } from '../../utils/core/endpoints';
import { isDefined } from '../../utils/variables';
import { TokenService } from '../authentication/token.service';
import {
  clearClientVersionOverride,
  CLIENT_STALE_RESPONSE_HEADER,
  CLIENT_VERSION_HEADER,
  getClientVersion,
} from '../client-version/client-version';
import { notifyVersionMismatch } from '../client-version/version-mismatch-events';
import { AuthServiceInternal } from './services/internal/auth-service';
import { FileVersionServiceInternal } from './services/internal/file-version-service';
import { ProjectServiceInternal } from './services/internal/project-service';
import { ReportServiceInternal } from './services/internal/report-service';
import { AccessTokenService } from './services/v0/access-token-service';
import { AccountService } from './services/v0/account-service';
import { AuthenticationService } from './services/v0/authentication-service';
import { CompanyService } from './services/v0/company-service';
import { DynamicAssetService } from './services/v0/dynamic-asset-service';
import { GeodataService } from './services/v0/geodata-service';
import { OrthoTileService } from './services/v0/ortho-tile-service';
import { PasswordResetTokenService } from './services/v0/password-reset-token-service';
import { PointCloudService } from './services/v0/point-cloud-service';
import { ProjectService } from './services/v0/project-service';
import { ScanService } from './services/v0/scan-service';
import { SharedViewService } from './services/v0/shared-view-service';
import { StaticAssetService } from './services/v0/static-asset-service';
import { TerrainModelService } from './services/v0/terrain-model-service';
import { TimelineServiceV0 } from './services/v0/timeline-service';
import { UserServiceV0 } from './services/v0/user-service';
import { AssetService } from './services/v1/asset-service';
import { AssetTagService } from './services/v1/asset-tag-service';
import { AuthService } from './services/v1/auth-service';
import { CompanyServiceV1 } from './services/v1/company-service';
import { CoordinateSystemService } from './services/v1/coordinate-system-service';
import { DongleServiceV1 } from './services/v1/dongle-service';
import { FileServiceV1 } from './services/v1/file-service';
import { FileVersionServiceV1 } from './services/v1/file-version-service';
import { FolderService as FolderServiceV1 } from './services/v1/folder-service';
import { GeoidService } from './services/v1/geoid-service';
import { PoiServiceV1 } from './services/v1/poi-service';
import { ProfileServiceV1 } from './services/v1/profile-service';
import { ProjectService as ProjectServiceV1 } from './services/v1/project-service';
import { SharedFolderServiceV1 } from './services/v1/shared-folder';
import { SharedViewServiceV1 } from './services/v1/shared-view-service';
import { TiledModelServiceV1 } from './services/v1/tiled-model-service';
import { UploadServiceV1 } from './services/v1/upload-service';
import { UserServiceV1 } from './services/v1/user-service';

/**
 *  What a 401 handler is told about the request that was refused.
 */
export type UnauthorizedContext = {
  /**
   *  Whether the refused request carried a session token. A 401 on a request that carried none is
   *  not an ended session but a refusal of what the request itself offered — the credentials of a
   *  sign-in, for one — and there is no session to renew or to end.
   */
  requestCarriedToken: boolean;
  /**
   *  Whether repeating this request is still on the table. `false` once it has already been retried
   *  after a renewal, which is what stops a permanently refused request from renewing forever.
   */
  canRetryAfterRenew: boolean;
};

/**
 *  Handles a 401 from the backend, and answers whether the refused request should be repeated.
 *
 *  Returning `true` is a promise that the token has been replaced with one worth trying; anything
 *  else means the handler has dealt with the 401 in its own way (typically by signing the user out)
 *  and the error should reach the caller unchanged.
 */
export type UnauthorizedHandler = (
  context: UnauthorizedContext,
) => boolean | Promise<boolean> | void;

/**
 *  Marks a request config that has already been retried after a renewal. A property on the config
 *  rather than state on the factory, because "already retried" is a fact about one request, and
 *  several can be in flight at once.
 */
type RetriedRequestConfig = InternalAxiosRequestConfig & { skymapRetriedAfterRenew?: boolean };

class SkyMapAxiosServiceFactory {
  static get instance() {
    return SkyMapAxiosServiceFactory._instance;
  }

  /**
   *  Because axiosInstance is not setup until setup, it is possible undefined.
   *  TODO: Remove dependency to
   */
  private static axiosInstance?: AxiosInstance;

  /**
   *  Axios instance for the internal API, which lives on its own gateway
   *  (see INTERNAL_API_DOMAIN) but shares the same authentication and
   *  client-version handling as the main API.
   */
  private static internalAxiosInstance?: AxiosInstance;

  /**
   * Callback to handle 401 (Unauthorized) errors.
   * Set via setUnauthorizedHandler method.
   */
  private static unauthorizedHandler?: UnauthorizedHandler;

  /**
   * Callback to handle 403 (Forbidden) errors.
   * Set via setForbiddenHandler method.
   */
  private static forbiddenHandler?: () => void;

  private static _instance: SkyMapAxiosServiceFactory = new SkyMapAxiosServiceFactory();

  /**
   *  Attaches the authentication bearer token to all request if available.
   * @param tokenService - The service that provides the authentication token.
   */
  static requestInterceptor(tokenService: TokenService) {
    return (config: InternalAxiosRequestConfig) => {
      const token = tokenService.get();

      config.headers = config.headers ?? {};
      if (isDefined(token)) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Tag every backend request with the client build version so the backend
      // can detect a stale client and respond with X-Skymap-Client-Stale (SK-5052).
      const clientVersion = getClientVersion();
      if (clientVersion) {
        config.headers[CLIENT_VERSION_HEADER] = clientVersion;
      }

      return config;
    };
  }

  /**
   *  Inspects response headers for `X-Skymap-Client-Stale: 1` (sent by the
   *  backend when the client version is out of sync) and dispatches a
   *  version-mismatch event so the UI can prompt the user to reload
   *  (SK-5052). Covers both success and error responses because the backend
   *  may signal a mismatch alongside a 4xx error status.
   *
   *  Any manual client-version override (set via the org-admin dev tool) is
   *  dropped here before notifying listeners, so the post-reload page does
   *  not send the same stale version again and trigger another mismatch.
   */
  static checkClientStale(headers: unknown): void {
    const h = headers as Record<string, string | undefined> | undefined;
    if (h?.[CLIENT_STALE_RESPONSE_HEADER] === '1') {
      clearClientVersionOverride();
      notifyVersionMismatch();
    }
  }

  /**
   *  Success-path response interceptor.
   * @param response - The axios response to inspect.
   */
  static responseInterceptor(response: AxiosResponse): AxiosResponse {
    SkyMapAxiosServiceFactory.checkClientStale(response.headers);
    return response;
  }

  /**
   *  Intercepts any error when executing requests. Prints the error and throws a promise rejection.
   * @param error
   */
  static requestErrorInterceptor(error: any) {
    console.log(JSON.stringify(error));
    return Promise.reject(error);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   *  Setup axios factory with the base url and token service.
   * @param url - The API base url.
   * @param tokenService - The service that provides the authentication token.
   */
  setup(url: string, tokenService: TokenService) {
    SkyMapAxiosServiceFactory.axiosInstance = SkyMapAxiosServiceFactory.createConfiguredInstance(
      url,
      tokenService,
    );

    SkyMapAxiosServiceFactory.internalAxiosInstance =
      SkyMapAxiosServiceFactory.createConfiguredInstance(resolveInternalApiUrl(), tokenService);
  }

  /**
   *  Creates an axios instance for the given base url with the shared
   *  authentication, client-version and 401/403 handling interceptors. Used for
   *  both the main API and the internal API, which run on separate gateways but
   *  behave identically otherwise.
   * @param url - The API base url.
   * @param tokenService - The service that provides the authentication token.
   */
  private static createConfiguredInstance(url: string, tokenService: TokenService): AxiosInstance {
    const instance = axios.create({
      baseURL: url,
    });

    instance.interceptors.request.use(
      SkyMapAxiosServiceFactory.requestInterceptor(tokenService),
      void SkyMapAxiosServiceFactory.requestErrorInterceptor,
    );

    instance.interceptors.response.use(
      (response) => SkyMapAxiosServiceFactory.responseInterceptor(response),
      async (error: unknown) => {
        // Backend may signal a version mismatch on an error response as
        // well as a success response — check both paths.
        if (isAxiosError(error) && error.response) {
          SkyMapAxiosServiceFactory.checkClientStale(error.response.headers);
        }
        // Handle 401 (Unauthorized) and 403 (Forbidden) errors
        if (isAxiosError(error)) {
          const status = error.response?.status;
          // Only handle errors from our backend API (not external services)
          // Check if the error is from our base URL
          const requestUrl =
            error.config?.url || (error.request as { responseURL?: string })?.responseURL || '';
          const baseUrl = url;
          const isBackendError =
            typeof requestUrl === 'string' &&
            (requestUrl.startsWith(baseUrl) || requestUrl.startsWith('/'));

          if (status === 401 && isBackendError) {
            // 401: Unauthorized - the token is gone, expired, or was never good enough.
            //
            // The handler decides which: it may be able to renew the session, in which case this
            // request is worth repeating once with the new token (the request interceptor above
            // reads it fresh, so nothing has to be copied across). Anything else means the handler
            // has signed the user out and the error belongs to the caller.
            if (SkyMapAxiosServiceFactory.unauthorizedHandler) {
              const config = error.config as RetriedRequestConfig | undefined;
              const alreadyRetried = config?.skymapRetriedAfterRenew === true;

              const shouldRetry = await SkyMapAxiosServiceFactory.unauthorizedHandler({
                // Without a config there is nothing to tell by, and the refusal is treated as a session's.
                requestCarriedToken: !isDefined(config) || isDefined(config.headers?.Authorization),
                canRetryAfterRenew: isDefined(config) && !alreadyRetried,
              });

              if (shouldRetry === true && isDefined(config) && !alreadyRetried) {
                config.skymapRetriedAfterRenew = true;
                return instance.request(config);
              }
            }
          } else if (status === 403 && isBackendError) {
            // 403: Forbidden - user is authenticated but lacks permission for this resource
            if (SkyMapAxiosServiceFactory.forbiddenHandler) {
              SkyMapAxiosServiceFactory.forbiddenHandler();
            }
          }
        }
        return Promise.reject(error);
      },
    );

    return instance;
  }

  /**
   * Register a callback to handle 401 (Unauthorized) errors.
   * This callback should either renew the session and ask for the request to be
   * repeated, or handle the 401 terminally by signing the user out.
   * @param handler - Callback function to execute on 401 errors. Returns whether
   * the refused request should be repeated; see {@link UnauthorizedHandler}.
   */
  setUnauthorizedHandler(handler: UnauthorizedHandler) {
    SkyMapAxiosServiceFactory.unauthorizedHandler = handler;
  }

  /**
   * Register a callback to handle 403 (Forbidden) errors.
   * This callback should redirect the user to an appropriate page (e.g., project list).
   * @param handler - Callback function to execute on 403 errors
   */
  setForbiddenHandler(handler: () => void) {
    SkyMapAxiosServiceFactory.forbiddenHandler = handler;
  }

  createAuthenticationServiceV0() {
    return new AuthenticationService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createAuthServiceV1() {
    return new AuthService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createAssetTagServiceV1() {
    return new AssetTagService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createPointCloudServiceV0() {
    return new PointCloudService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createDynamicAssetServiceV0() {
    return new DynamicAssetService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createStaticAssetServiceV0() {
    return new StaticAssetService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createSharedViewServiceV0() {
    return new SharedViewService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createScanServiceV0() {
    return new ScanService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createOrthoTileServiceV0() {
    return new OrthoTileService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createTerrainModelServiceV0() {
    return new TerrainModelService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createAccountServiceV0() {
    return new AccountService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createGeodataServiceV0() {
    return new GeodataService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createProjectServiceV0(cancelTokenSource?: CancelTokenSource) {
    return new ProjectService(SkyMapAxiosServiceFactory.axiosInstance!, cancelTokenSource);
  }

  createCompanyServiceV0() {
    return new CompanyService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createAccessTokenServiceV0() {
    return new AccessTokenService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createUserServiceV0() {
    return new UserServiceV0(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createProjectServiceV1() {
    return new ProjectServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createFileServiceV1() {
    return new FileServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createCompanyServiceV1() {
    return new CompanyServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createFileVersionServiceV1() {
    return new FileVersionServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createFolderServiceV1() {
    return new FolderServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createTimelineServiceV0() {
    return new TimelineServiceV0(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createTiledModelServiceV1() {
    return new TiledModelServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createSharedFolderServiceV1() {
    return new SharedFolderServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createCoordinateSystemServiceV1() {
    return new CoordinateSystemService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createGeoidServiceV1() {
    return new GeoidService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createAssetServiceV1() {
    return new AssetService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createUserServiceV1() {
    return new UserServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createUploadServiceV1() {
    return new UploadServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createPoiServiceV1() {
    return new PoiServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createSharedViewServiceV1() {
    return new SharedViewServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createDongleServiceV1() {
    return new DongleServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createPasswordResetTokenServiceV0() {
    return new PasswordResetTokenService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createProfileServiceV1() {
    return new ProfileServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createAuthServiceInternal() {
    return new AuthServiceInternal(SkyMapAxiosServiceFactory.internalAxiosInstance!);
  }

  createFileVersionServiceInternal() {
    return new FileVersionServiceInternal(SkyMapAxiosServiceFactory.internalAxiosInstance!);
  }

  createProjectServiceInternal() {
    return new ProjectServiceInternal(SkyMapAxiosServiceFactory.internalAxiosInstance!);
  }

  createReportServiceInternal() {
    return new ReportServiceInternal(SkyMapAxiosServiceFactory.internalAxiosInstance!);
  }
}

export { SkyMapAxiosServiceFactory };
